import React, { useState, useEffect } from "react";
import { Formik, Field } from "formik";
import { Redirect } from "react-router";

import endOfToday from "date-fns/endOfToday";
import differenceInYears from "date-fns/differenceInYears";

import { get, post } from "../api";

import PrimaryCTAButton from "../../components/PrimaryCTA";
import Form from "../../components/Form";
import FormField from "../../components/FormField";
import {
    FormikInput,
    FormikBirthdateInput,
    FormikSearchableDropdown,
} from "../../components/Input";
import Label from "../../components/Label";

import PageContainer from "../components/PageContainer";

import { resourceUrls } from "../../config";
import { handleGet, handlePost } from "../api/handler";

const phoneCharsToRemove = /[\s\h()+\uff0b\-\u2011\.\u061C\u200E\u200F\u202A\u202B\u202C\u202D\u202E]|ext|x/ig;
const phoneRegex = /^\d{7,15}$/;
const phoneNumberMaxLength = 20;

const initialValues = {
    dateOfBirth: "",
    phoneNumber: null,
    nationality: null,
    gender: null,
    genderDescription: null,
};

const createUKDate = date => {
    const dateStringArray = date.split("/");
    return new Date(
        dateStringArray[2],
        dateStringArray[1] - 1,
        dateStringArray[0]
    );
};

const is18OrOver = dateOfBirth => {
    return differenceInYears(endOfToday(), dateOfBirth) >= 18;
};

const isValidDate = date => {
    return date instanceof Date && !isNaN(date);
};

const formatDateForAPI = date => {
    const dateStringArray = date.split("/");
    return `${dateStringArray[2]}-${dateStringArray[1]}-${dateStringArray[0]}`;
};

const validator = values => {
    let errors = {};
    if (!values.dateOfBirth || !isValidDate(createUKDate(values.dateOfBirth))) {
        errors.dateOfBirth = "Required";
    } else if (values.dateOfBirth.length < 10) {
        errors.dateOfBirth = "Please enter a date in DD/MM/YYYY format.";
    } else if (
        // regex to test for digits and optional hyphen, plus, space
        !is18OrOver(createUKDate(values.dateOfBirth))
    ) {
        errors.dateOfBirth = "Sorry - you must be age 18 or over to apply.";
    }
    if (!values.phoneNumber) {
        errors.phoneNumber = "Required";
    } else if (!phoneRegex.test(values.phoneNumber.replace(phoneCharsToRemove, '')) || values.phoneNumber.length >= phoneNumberMaxLength) {
        errors.phoneNumber = "Invalid phone number";
    }
    if (!values.nationality) {
        errors.nationality = "Required";
    }
    if (!values.gender) {
        errors.gender = "Required";
    }
    if (values.gender === "Other" && !values.genderDescription) {
        errors.gender = "Required";
    }
    return errors;
};

const getNationalitities = async (
    setIsLoading,
    setIsLoggedIn,
    setError,
    setNationalityOptions
) => {
    await handleGet({
        getApiCall: () =>
            get({ url: resourceUrls.personalContact.getNationalities }),
        setIsLoading,
        setIsLoggedIn,
        setError,
        dataTransform: nationalities =>
            Object.keys(nationalities).map(n => ({
                key: n,
                value: n,
                label: nationalities[n],
            })),
        setData: setNationalityOptions,
    });
};

const getGenders = async (
    setIsLoading,
    setIsLoggedIn,
    setError,
    setGenderOptions
) => {
    await handleGet({
        getApiCall: () => 
            get({ url: resourceUrls.personalContact.getGenders }),
        setIsLoading,
        setIsLoggedIn,
        setError,
        dataTransform: genders =>
            Object.keys(genders).map(g => ({
                key: g,
                value: g,
                label: genders[g],
            })),
        setData: setGenderOptions,
    });
};

const submitPersonalContact = (
    setIsLoggedIn,
    setError,
    setFormSubmissionComplete
) => async (values, actions) => {
    const body = {
        dateOfBirth: formatDateForAPI(values.dateOfBirth),
        phoneNumber: values.phoneNumber,
        nationality: values.nationality,
        gender: values.gender,
        genderDescription: values.genderDescription,
        applicationStep: "PERSONAL_CONTACT",
    };

    await handlePost({
        postApiCall: () =>
            post({
                url: resourceUrls.personalContact.submitPersonalContact,
                body: body,
            }),
        setIsLoggedIn: setIsLoggedIn,
        setError: setError,
        setIsSubmissionComplete: setFormSubmissionComplete,
    });

    actions.setSubmitting(false);
};

const PersonalContact = () => {
    const [isLoadingNationalities, setIsLoadingNationalities] = useState(true);
    const [isLoadingGenders, setIsLoadingGenders] = useState(true);
    const isLoading = isLoadingNationalities || isLoadingGenders;
    const [isLoggedIn, setIsLoggedIn] = useState(true);
    const [error, setError] = useState(null);
    const [formSubmissionComplete, setFormSubmissionComplete] = useState(false);

    const [nationalityOptions, setNationalityOptions] = useState([]);
    const [genderOptions, setGenderOptions] = useState([]);

    useEffect(() => {
        getNationalitities(
            setIsLoadingNationalities,
            setIsLoggedIn,
            setError,
            setNationalityOptions
        );
    }, []);

    useEffect(() => {
        getGenders(
            setIsLoadingGenders,
            setIsLoggedIn,
            setError,
            setGenderOptions
        );
    }, []);

    if (formSubmissionComplete) {
        return (
            <Redirect
                push
                to={{
                    pathname: "/secure/apply",
                }}
            />
        );
    }

    return (
        <PageContainer
            isLoading={isLoading}
            error={error}
            isLoggedIn={isLoggedIn}
        >
            <h2>What are your personal details?</h2>
            <Formik
                initialValues={initialValues}
                validate={validator}
                validateOnBlur={false}
                validateOnChange={true}
                onSubmit={submitPersonalContact(
                    setIsLoggedIn,
                    setError,
                    setFormSubmissionComplete
                )}
                render={({ values, handleSubmit, isSubmitting }) => (
                    <Form onSubmit={handleSubmit}>
                        <FormField>
                            <Label htmlFor="dateOfBirth">
                                Birth date (DD/MM/YYYY)
                            </Label>
                            <Field
                                id="dateOfBirth"
                                data-testid="personal-contact-birthdate"
                                name="dateOfBirth"
                                component={FormikBirthdateInput}
                            />
                        </FormField>
                        <FormField>
                            <Label htmlFor="phoneNumber">Phone number</Label>
                            <Field
                                id="phoneNumber"
                                data-testid="personal-contact-phone-number"
                                name="phoneNumber"
                                component={FormikInput}
                            />
                        </FormField>
                        <FormField>
                            <Label htmlFor="gender">Gender</Label>
                            <Field
                                id="gender"
                                data-testid="personal-contact-gender"
                                name="gender"
                                options={genderOptions}
                                component={FormikSearchableDropdown}
                            />
                        </FormField>
                        {values.gender === "Other" && (
                            <FormField>
                                <Label htmlFor="email">Add description</Label>
                                <Field
                                    id="genderDescription"
                                    data-testid="personal-contact-gender-description"
                                    name="genderDescription"
                                    component={FormikInput}
                                />
                            </FormField>
                        )}
                        <FormField>
                            <Label htmlFor="nationality">Nationality</Label>
                            <Field
                                id="nationality"
                                data-testid="personal-contact-nationality"
                                name="nationality"
                                options={nationalityOptions}
                                component={FormikSearchableDropdown}
                            />
                        </FormField>
                        <PrimaryCTAButton type="submit" data-testid="next-button" disabled={isSubmitting}>
                            Next →
                        </PrimaryCTAButton>
                    </Form>
                )}
            />
        </PageContainer>
    );
};

export default PersonalContact;
